/** The leaderboard-row half of the comparison: the row's own top-level `user_id` wire field. */
export interface ViewerMatchableRow {
  user_id: number;
}

/** The logged-in-user half: `User.id`, as `SharedSelectors.getUserDetails()` supplies it. */
export interface ViewerMatchableUser {
  id: number;
}

/**
 * The "is this row me?" rule for a leaderboard, in ONE place.
 *
 * Matched on the row's top-level `user_id` (`LeaderboardEntry`, Leaderboard.tsx) against the logged-in
 * user's own `id` (`User.tsx`) -- NOT on `row.user.id`, the nested object the same call sites read
 * first/last names from. Both compile, which is exactly why the comparison is written once here rather
 * than six times across the two screens (`LeaderboardsContainer`'s podium tiles, inline rank rows and
 * duplicate-suppression check, and `OverviewLeaderboard`'s own three) -- a typo in any one of those
 * would have produced a screen that highlights the wrong person, or nobody, with nothing to catch it.
 *
 * Takes the viewer as possibly absent and returns `false` for that case, so callers do not repeat a
 * null check either: both screens read the viewer from an app-wide selector that is typed nullable.
 *
 * @param row - the leaderboard row (or podium entry) being rendered.
 * @param viewer - the logged-in user, or `null`/`undefined` when there is none.
 * @returns true when this row belongs to the logged-in user.
 */
export const isViewerRow = (row: ViewerMatchableRow, viewer: ViewerMatchableUser | null | undefined): boolean =>
  viewer != null && row.user_id === viewer.id;
